import { useState, type ChangeEvent } from "react"
import { Check, Send } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "../Button/Button"

export interface ContactFormProps {
  label?: string
  placeholder?: string
  value?: string
  defaultValue?: string
  sent?: boolean
  defaultSent?: boolean
  rows?: number
  sendLabel?: string
  sentLabel?: string
  onChange?: (value: string) => void
  onSubmit?: (value: string) => void
  className?: string
}

export function ContactForm({
  label = "Message",
  placeholder = "Your message...",
  value,
  defaultValue = "",
  sent,
  defaultSent = false,
  rows = 5,
  sendLabel = "Send",
  sentLabel = "Sent!",
  onChange,
  onSubmit,
  className,
}: ContactFormProps) {
  const [internalValue, setInternalValue] = useState(defaultValue)
  const [internalSent, setInternalSent] = useState(defaultSent)

  const isValueControlled = value !== undefined
  const isSentControlled = sent !== undefined
  const currentValue = isValueControlled ? value : internalValue
  const currentSent = isSentControlled ? sent : internalSent

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value
    if (!isValueControlled) setInternalValue(next)
    onChange?.(next)
  }

  const handleSend = () => {
    if (!currentValue.trim() || currentSent) return
    onSubmit?.(currentValue)
    if (!isSentControlled) setInternalSent(true)
  }

  const disabled = !currentValue.trim() || currentSent

  return (
    <div
      className={cn(
        "bg-card border border-border rounded-md p-5",
        className,
      )}
    >
      <label className="text-caption text-muted-foreground mb-1.5 block">{label}</label>
      <textarea
        value={currentValue}
        onChange={handleChange}
        rows={rows}
        placeholder={placeholder}
        className="w-full bg-muted border border-border rounded-md px-3 py-2.5 text-body text-foreground outline-none resize-none placeholder:text-muted-foreground/50 focus:border-ring/60 focus:ring-0 focus:outline-none transition-colors"
      />
      <Button
        size="sm"
        disabled={disabled}
        onClick={handleSend}
        className="mt-3 gap-1.5 text-label"
      >
        {currentSent ? (
          <>
            <Check size={12} strokeWidth={2} /> {sentLabel}
          </>
        ) : (
          <>
            <Send size={12} strokeWidth={1.5} /> {sendLabel}
          </>
        )}
      </Button>
    </div>
  )
}
